import { formatCurrency } from '../lib/format.js';

/**
 * DebtProgressBar — visual progress bar for a debt instrument.
 *
 * Props:
 *   paid_cents  : integer — amount already repaid
 *   total_cents : integer — initial capital
 *   label       : string — optional override label
 *   size        : 'sm' | 'md' | 'lg'
 *   isOverdue   : boolean — affects bar color
 */
export default function DebtProgressBar({
  paid_cents = 0,
  total_cents = 0,
  label,
  size = 'md',
  isOverdue = false,
}) {
  const pct = total_cents > 0 ? Math.min(100, Math.round((paid_cents / total_cents) * 100)) : 0;

  const barColor = isOverdue
    ? 'bg-amber-500'
    : pct >= 100
    ? 'bg-emerald-500'
    : 'bg-sky-500';

  const heightClass = size === 'sm' ? 'h-1.5' : size === 'lg' ? 'h-3' : 'h-2';

  const textClass = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-xs';

  return (
    <div className="space-y-1 w-full">
      <div className={`w-full bg-slate-100 rounded-full overflow-hidden ${heightClass}`}>
        <div
          className={`${barColor} ${heightClass} rounded-full transition-all duration-300`}
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label ?? `Remboursement : ${pct}%`}
        />
      </div>
      {size !== 'sm' && (
        <div className={`flex justify-between ${textClass} text-slate-500`}>
          <span>
            {formatCurrency(paid_cents)}{' '}
            <span className="text-slate-400">/ {formatCurrency(total_cents)}</span>
          </span>
          <span className="font-medium text-slate-700 tabular-nums">{pct}&nbsp;%</span>
        </div>
      )}
    </div>
  );
}
